if (!customElements.get('stock-display')) {
    class StockDisplay extends HTMLElement {
        productAvailabilityCache = null;
        
        constructor() {
            super();
            this.currentStoreCookie = this.getCookie('currentStore');
            this.stockDisplayQueryId = this.dataset.stockDisplayQueryId;
            this.blockClickCollect = this.dataset.blockClickCollect === 'true';
            this.provinceSelectionElement = this.querySelector('#stock-display__province-selection');
            this.storeSelectionElement = this.querySelector('#stock-display__store-selection');
            this.storeMessageElement = this.querySelector('#stock-display__message');
            this.storeNameElement = this.querySelector('.stock-display__store-name');
            this.storeHeaderElement = this.querySelector('.stock-display__store-header');
            this.availabilitySectionElement = this.querySelector('.stock-display__availability-section');
            this.messageContainerElement = this.querySelector('.stock-display__message-container');
            this.storeCheckElement = this.querySelector('.stock-display__row.store-check');
            this.storeToolbarElement = this.querySelector('.stock-display__toolbar');
            this.instoreItemElement = this.querySelector('.stock-display__availability-item:nth-child(1)');
            this.clickCollectItemElement = this.querySelector('.stock-display__availability-item:nth-child(2)');
            this.deliveryStatusElement = this.querySelector('#stock-display__status-delivery .stock-display__placeholder');
            this.collapsibleTriggerElements = this.querySelectorAll('.stock-display__collapsible-trigger');
            this.collapsibleElement = this.querySelector('#stock-display__collapsible');
            this.currentProductId = this.dataset.productId;
            this.currentVariantId = this.dataset.variantId;
            this.currentLocationId = this.currentStoreCookie?.dataId ?? '';
            this.inventoryByVariantId = {};
            this.callStoreText = this.dataset.callStoreText;
            this.inStockText = this.dataset.inStockText;
            this.outOfStockText = this.dataset.outOfStockText;
            this.init();
        }
        
        init() {
            const productId = this.currentProductId;
            const variantId = this.currentVariantId;
            const locationId = this.currentLocationId;
            this.loadProductAvailability(productId).then(() => {
                this.createSelections(variantId);
                this.setCurrentStore();
                this.setStockAvailability(variantId, locationId);
            });
            this.collapsibleEvent();
            this.onVariantChange();
        }
        
        async fetchProductAndLocationData(productId) {
            const url = `/apps/datajet/task/${this.stockDisplayQueryId}`;
            const payload = { productId };
            
            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                });
                
                const responseJson = await response.json();
                if (response.ok && responseJson?.data?.product && responseJson?.data?.locations) {
                    return responseJson.data;
                } else {
                    console.error("Invalid response structure:", responseJson);
                    return {};
                }
            } catch (error) {
                console.error("Fetch Error:", error);
                return {};
            }
        }
        
        async getVariantAvailabilityByLocationId(variantId, locationId) {
            const stockAvailability = await this.loadProductAvailability(this.currentProductId);
            const variantKey = `gid://shopify/ProductVariant/${variantId}`;
            const locationKey = `gid://shopify/Location/${locationId}`;
            const variantData = stockAvailability[variantKey];
            
            if (!variantData) {
                return {
                    storeStockStatus: 'out-of-stock',
                    warehouseStockStatus: 'out-of-stock',
                    clickCollectStatus: 'unavailable',
                };
            }
            
            const onlineStoreLocation = Object.values(variantData.locations || {}).find(loc => {
                return loc.isWarehouse === true;
            });
            
            const storeLocationData = variantData.locations?.[locationKey];
            
            // console.log('Store Location Data:', {
            //     found: !!storeLocationData,
            //     name: storeLocationData?.name,
            //     totalAvailable: storeLocationData?.totalAvailable
            // });
            
            if (!storeLocationData) {
                const warehouseStatus = onlineStoreLocation ? 
                    (onlineStoreLocation.totalAvailable > 0 ? 'in-stock' : 'out-of-stock') 
                    : 'out-of-stock';
                
                return {
                    storeStockStatus: '',
                    warehouseStockStatus: warehouseStatus,
                    clickCollectStatus: 'unavailable'
                };
            }
            
            const onlineStoreStatus = onlineStoreLocation ? 
                (onlineStoreLocation.totalAvailable > 0 ? 'in-stock' : 'out-of-stock') 
                : 'out-of-stock';

            return {
                ...storeLocationData,
                warehouseStockStatus: onlineStoreStatus
            };
        }
        
        
        async loadProductAvailability(productId) {
            if (this.productAvailabilityCache) return this.productAvailabilityCache;
            
            const response = await this.fetchProductAndLocationData(productId);
            const { product, locations } = response;
            
            if (!product || !locations) {
                this.style.display = "none";
                return {};
            }
            
            product.variants.edges.forEach(({ node: variant }) => {
                const variantId = variant.id;
                const hideStockDisplay = variant.hideStockDisplay?.value ?? null;
                const locationsMap = {};
                let totalWarehouseAvailable = 0;
                let totalStoreAvailable = 0;
                let variantAvailableForSale = variant.availableForSale;
                
                variant.inventoryItem.inventoryLevels.edges.forEach(({ node: level }) => {
                    const loc = level.location;
                    const locationId = loc.id;
                    const availableQuantity = level.quantities.find(q => q.name === 'available')?.quantity ?? 0;
                    
                    if (!locationsMap[locationId]) {
                        locationsMap[locationId] = {
                            name: loc.name,
                            frontEndName: loc.storeFrontendName?.value || loc.name,
                            storeHidden: loc.storeHidden?.value === 'true',
                            enableForPickup: loc.enableForPickup?.value === 'true',
                            storeEnable: loc.storeStockAvailability?.value === 'true',
                            warehouseEnable: loc.warehouseStockAvailability?.value === 'true',
                            inStockThreshold: parseInt(loc.inStockThreshold?.value) ?? 0,
                            outOfStockThreshold: parseInt(loc.outOfStockThreshold?.value) ?? 0,
                            storeAvailable: 0,
                            warehouseAvailable: 0,
                            totalAvailable: 0,
                            storeStockStatus: null,
                            warehouseStockStatus: 'out-of-stock',
                            isWarehouse: loc.isWarehouse?.value === 'true'
                        };
                    }
                    
                    const store = locationsMap[locationId];
                    store.totalAvailable += availableQuantity;
                    
                    if (store.storeEnable) {
                        store.storeAvailable += availableQuantity;
                        totalStoreAvailable += availableQuantity;
                    }
                    
                    if (store.warehouseEnable) {
                        store.warehouseAvailable += availableQuantity;
                        totalWarehouseAvailable += availableQuantity;
                    }

                    store.storeStockStatus = store.storeAvailable > 0 ? 'available' : 'unavailable';
                    store.warehouseStockStatus = store.warehouseAvailable > 0 ? 'available' : 'unavailable';
                    store.clickCollectStatus =
                        (this.blockClickCollect || store.totalAvailable <= 0 || store.enableForPickup !== true) ? 'unavailable' : 'available';
                    if (hideStockDisplay === 'true') {
                        store.storeStockStatus = 'unavailable';
                    }
                });
                
                locations.edges.forEach(({ node }) => {
                    const locationId = node.id;
                    if (!locationsMap[locationId]) {
                        locationsMap[locationId] = {
                            name: node.name,
                            frontEndName: node.storeFrontendName?.value || node.name,
                            storeHidden: node.storeHidden?.value === 'true',
                            enableForPickup: node.enableForPickup?.value === 'true',
                            storeEnable: node.storeStockAvailability?.value === 'true',
                            warehouseEnable: node.warehouseStockAvailability?.value === 'true',
                            inStockThreshold: parseInt(node.inStockThreshold?.value) ?? 0,
                            outOfStockThreshold: parseInt(node.outOfStockThreshold?.value) ?? 0,
                            storeAvailable: 0,
                            warehouseAvailable: 0,
                            totalAvailable: 0,
                            storeStockStatus: 'unavailable',
                            warehouseStockStatus: 'unavailable',
                            isWarehouse: node.isWarehouse?.value === 'true',
                            clickCollectStatus: 'unavailable'
                        };
                    }
                });
                
                this.inventoryByVariantId[variantId] = {
                    hideStockDisplay,
                    totalStoreAvailable,
                    totalWarehouseAvailable,
                    locations: locationsMap
                };
            });
            
            this.locationsList = locations.edges.map(edge => edge.node);
            this.productAvailabilityCache = this.inventoryByVariantId;
            return this.productAvailabilityCache;
        }
        
        async createSelections(variantId) {
            const locations = this.locationsList;
            const self = this;
            if (!locations || locations.length === 0) {
                console.warn("No locations available.");
                return;
            }
            const currentLocationId = this.currentStoreCookie?.dataId ?? '';
            const currentLocationProvince = this.currentStoreCookie?.province ?? '';
            
            // Province selection setup
            const provinceSelectionElement = this.provinceSelectionElement;
            const provinces = [...new Set(locations.map(location => location.address.province))];
            
            const provinceChoices = new Choices(provinceSelectionElement, {
                searchEnabled: false,
                searchChoices: false,
                itemSelectText: '',
                placeholder: "Select state",
                callbackOnCreateTemplates: function (template) {
                    return {
                        item: (classNames, data) => {
                            let prefix = 'State:';
                            let placeholderClass = '';
                            if (data.label === classNames.placeholderValue) {
                                prefix = '';
                                placeholderClass = 'choices__placeholder';
                            }
                            
                            return template(`
                                <div class="${classNames.classNames.item.join(' ')} ${placeholderClass} ${classNames.classNames.itemSelectable.join(' ')}" data-item data-id="${data.id}" data-value="${data.value}">
                                    ${prefix} ${data.label}
                                </div>
                            `);
                        }
                    };
                },
                choices: provinces.map(province => ({
                    value: province,
                    label: province,
                    selected: province === currentLocationProvince,
                })),
            });
            
            // Store selection setup, parallel fetching availability
            const storeChoicesList = await Promise.all(locations.map(async location => {
                const locationId = location.id.replace('gid://shopify/Location/', '');
                const stockData = await this.getVariantAvailabilityByLocationId(variantId, locationId);
                const frontEndName = location.storeFrontendName?.value || location.name;
                const storeHidden = location.storeHidden?.value === 'true';
                const storeEnable = location.storeStockAvailability?.value === 'true';
                
                if (storeHidden || !storeEnable) {
                    return null;
                }
                
                return {
                    value: frontEndName,
                    label: frontEndName,
                    selected: locationId === currentLocationId,
                    customProperties: {
                        id: locationId,
                        province: location.address.province,
                        storeHidden: stockData.storeHidden,
                        enableForPickup: stockData.enableForPickup,
                        storeEnable: stockData.storeEnable,
                        stockStatus: stockData.storeStockStatus || 'out-of-stock',
                    },
                };
            }));
            
            const storeChoicesListUnselected = await Promise.all(locations.map(async location => {
                const locationId = location.id.replace('gid://shopify/Location/', '');
                const stockData = await this.getVariantAvailabilityByLocationId(variantId, locationId);
                const frontEndName = location.storeFrontendName?.value || location.name;
                const storeHidden = location.storeHidden?.value === 'true';
                const storeEnable = location.storeStockAvailability?.value === 'true';

                if (storeHidden || !storeEnable) {
                    return null;
                }
                
                return {
                    value: frontEndName,
                    label: frontEndName,
                    customProperties: {
                        id: locationId,
                        province: location.address.province,
                        storeHidden: stockData.storeHidden,
                        enableForPickup: stockData.enableForPickup,
                        storeEnable: stockData.storeEnable,
                        stockStatus: stockData.storeStockStatus || 'out-of-stock',
                    },
                };
            }));

            const filteredStoreChoicesList = storeChoicesList.filter(choice => choice !== null);
            const filteredStoreChoicesListUnselected = storeChoicesListUnselected.filter(choice => choice !== null);
            
            const selectedProvince = provinceSelectionElement.value;
            const initialFilteredStores = selectedProvince
                ? filteredStoreChoicesList.filter(choice => choice.customProperties.province === selectedProvince)
                : [];
            
            const storeChoices = new Choices(this.storeSelectionElement, {
                searchEnabled: false,
                searchChoices: false,
                itemSelectText: '',
                callbackOnCreateTemplates: function (template) {
                    return {
                        choice: (classNames, data) => {
                            const storeHidden = data.customProperties?.storeHidden;
                            const storeEnable = data.customProperties?.storeEnable;
                            const enableForPickup = data.customProperties?.enableForPickup;
                            const stockStatus = data.customProperties?.stockStatus;
                            const normalizedStatus =
                                (stockStatus === 'in-stock' || stockStatus === 'available' || stockStatus === 'low-in-stock')
                                    ? 'available' : 'unavailable';
                            if (storeEnable === false) {
                                data.disabled = true;
                            }
                            const statusLabel = normalizedStatus === 'unavailable'
                                ? `<div class="stock-display__status out-of-stock"><div class="stock-display__dot"></div><div class="stock-display__label">${self.outOfStockText}</div></div>`
                                : `<div class="stock-display__status in-stock"><div class="stock-display__dot"></div><div class="stock-display__label">${self.inStockText}</div></div>`;
                            return template(`
                                <div class="${(storeHidden === true) ? 'hidden' : ''} ${classNames.classNames.item.join(' ')} ${classNames.classNames.itemChoice.join(' ')} ${data.selected ? classNames.classNames.selectedState.join(' ') : ''} ${data.disabled ? classNames.classNames.itemDisabled.join(' ') : classNames.classNames.itemSelectable.join(' ')}" data-choice data-id="${data.id}" data-value="${data.value}" ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'}>
                                    <span class="store-name">${data.label}</span> ${statusLabel}
                                </div>
                            `);
                        }
                    };
                },
                placeholder: "Select store",
                choices: initialFilteredStores
            });
            
            this.provinceChoices = provinceChoices;
            this.storeChoices = storeChoices;
            this.storeChoicesList = filteredStoreChoicesListUnselected;
            
            provinceSelectionElement.addEventListener('change', (event) => {
                const selectedProvince = event.target.value;
                
                // Filter and update store choices
                const filteredChoices = this.storeChoicesList.filter(choice =>
                    choice.customProperties.province === selectedProvince
                );
                
                this.storeChoices.removeActiveItems();
                this.storeChoices.removeHighlightedItems();
                this.storeChoices.clearChoices(true, true);
                this.storeChoices.setChoices(filteredChoices, 'value', 'label', true);
            });
            
            this.storeSelectionElement.addEventListener('change', (event) => {
                if (this.deliveryStatusElement) this.deliveryStatusElement.textContent = "Loading...";
                const selectedStore = event.target.value;
                
                const filteredChoices = this.storeChoicesList.filter(choice =>
                    choice.value === selectedStore
                );
                
                const selectedDataId = filteredChoices[0].customProperties.id;
                const selectedProvince = filteredChoices[0].customProperties.province;
                const selectedStoreStockAvailability = filteredChoices[0].customProperties.stockStatus;
                
                this.storeChoices.setChoiceByValue(filteredChoices[0].value);
                this.provinceChoices.setChoiceByValue(selectedProvince);
                
                const storeData = {
                    name: selectedStore,
                    dataId: selectedDataId,
                    province: selectedProvince,
                    storeStockAvailability: selectedStoreStockAvailability
                };
                
                const collapsibleElement = this.collapsibleElement;
                this.toggleHide(collapsibleElement);
                this.setCookie('currentStore', storeData, 30);
                this.currentStoreCookie = this.getCookie('currentStore');
                this.setCurrentStore();
                this.setStockAvailability(this.currentVariantId, selectedDataId);
            });
        }

        async setCurrentStore() {
            if (this.currentStoreCookie) {
                const location = this.locationsList.find(loc => loc.id.replace('gid://shopify/Location/', '') === this.currentStoreCookie.dataId);
                const displayName = location?.storeFrontendName?.value || this.currentStoreCookie.name;

                await this.updateAttributeCart('_pickup_store_name', displayName);
                try {
                    window.dispatchEvent(new CustomEvent('pickupStore:updated', {
                        detail: {
                            storeName: displayName,
                            storeId: this.currentStoreCookie.dataId
                        }
                    }));
                } catch (e) {
                    // no-op
                }

                this.storeNameElement.innerHTML = displayName;
                this.currentLocationId = this.currentStoreCookie.dataId;
                this.toggleShow(this.storeHeaderElement);
                this.toggleShow(this.availabilitySectionElement);
                this.toggleHide(this.storeCheckElement);
            } else {
                this.toggleHide(this.storeHeaderElement);
                this.toggleHide(this.availabilitySectionElement);
                this.toggleHide(this.messageContainerElement);
                this.toggleShow(this.storeCheckElement);
                if (this.deliveryStatusElement) this.deliveryStatusElement.innerHTML = '';
                if (this.instoreItemElement) this.instoreItemElement.setAttribute('data-status', '');
                if (this.clickCollectItemElement) this.clickCollectItemElement.setAttribute('data-status', '');
                this.currentLocationId = '';
            }
        }

        async updateAttributeCart(key, value) {
            try {
                const response = await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        attributes: {
                            [key]: value || '',
                        },
                    }),
                });

                const data = await response.json();
                console.log('✅ Cart attribute updated:', data.attributes);
                return data;
            } catch (error) {
                console.error('❌ Error updating pickup store attribute:', error);
            }
        }

        setStockStatusHtml(element, status) {
            if (element) {
                if (status === 'out-of-stock' || status === 'unavailable' || status === 'call-store') {
                    element.innerHTML = `<div class="stock-display__status out-of-stock"><div class="stock-display__dot"></div><div class="stock-display__label">${this.outOfStockText}</div></div>`;
                } else if (status === 'in-stock' || status === 'low-in-stock' || status === 'available') {
                    element.innerHTML = `<div class="stock-display__status in-stock"><div class="stock-display__dot"></div><div class="stock-display__label">${this.inStockText}</div></div>`;
                }
            }
        }

        normalizeStatus(status) {
            if (!status) return 'unavailable';
            if (status === 'in-stock' || status === 'available' || status === 'low-in-stock') {
                return 'available';
            }
            return 'unavailable';
        }

        async setStockAvailability(variantId, locationId) {
            const data = await this.getVariantAvailabilityByLocationId(variantId, locationId);
            if (data !== null) {
                const warehouseStatus = data.warehouseStockStatus || 'out-of-stock';
                this.setStockStatusHtml(this.deliveryStatusElement, warehouseStatus);

                if (!locationId || !this.currentStoreCookie) {
                    this.storeMessageElement.innerHTML = '';
                    this.toggleHide(this.messageContainerElement);
                    this.toggleHide(this.storeToolbarElement);
                    return;
                }

                if (this.instoreItemElement) {
                    this.instoreItemElement.setAttribute('data-status', data.storeStockStatus || '');
                }
                if (this.clickCollectItemElement) {
                    this.clickCollectItemElement.setAttribute('data-status', data.clickCollectStatus || '');
                }

                const normalizedStoreStatus = this.normalizeStatus(data.storeStockStatus);
                if (normalizedStoreStatus === 'unavailable') {
                    const selectedLocation = this.locationsList?.find(
                        (loc) => loc.id.replace('gid://shopify/Location/', '') === locationId
                    );
                    const displayName = selectedLocation?.storeFrontendName?.value || selectedLocation?.name || this.storeNameElement?.textContent;
                    this.storeMessageElement.innerHTML = `Sorry, this product is unavailable at ${displayName}.`;
                    this.toggleShow(this.messageContainerElement);
                    this.toggleShow(this.storeToolbarElement);
                } else {
                    this.storeMessageElement.innerHTML = '';
                    this.toggleHide(this.messageContainerElement);
                    this.toggleHide(this.storeToolbarElement);
                }
            }
        }
        
        collapsibleEvent() {
            const collapsibleElement = this.collapsibleElement;
            this.collapsibleTriggerElements.forEach(trigger =>
                trigger.addEventListener('click', () => this.toggleShowHide(collapsibleElement))
            );
        }
        
        toggleShowHide(element) {
            if (!element) return;
            element.classList.toggle('hide');
            this.classList.toggle('collapsible-open');
            this.reCalculateAccordionHeight();
        }
        
        toggleHide(element) {
            if (!element) return;
            element.classList.add('hide');
        }
        
        toggleShow(element) {
            if (!element) return;
            element.classList.remove('hide');
        }
        
        reCalculateAccordionHeight() {
            const accordionContent = this.closest('.accordion-content');
            if (accordionContent) {
                accordionContent.style.maxHeight = `${accordionContent.scrollHeight + 15}px`;
            }
        }
        
        setCookie(name, value, days = 7) {
            const expires = new Date();
            expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
            const cookieValue = JSON.stringify(value);
            document.cookie = `${name}=${encodeURIComponent(cookieValue)};expires=${expires.toUTCString()};path=/`;
        }
        
        getCookie(name) {
            const nameEQ = name + "=";
            const ca = document.cookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) === ' ') c = c.substring(1, c.length);
                if (c.indexOf(nameEQ) === 0) {
                    try {
                        return JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)));
                    } catch (e) {
                        console.error("Error parsing cookie value:", e);
                        return null;
                    }
                }
            }
            return null;
        }

        updateStoreChoicesForVariant(variantId) {
            this.productAvailabilityCache = null;

            if (!this.storeChoices || !this.locationsList) return;

            const locations = this.locationsList;
            const currentLocationId = this.currentStoreCookie?.dataId ?? '';

            const updatedStoreChoicesList = Promise.all(locations.map(async location => {
                const locationId = location.id.replace('gid://shopify/Location/', '');
                const stockData = await this.getVariantAvailabilityByLocationId(variantId, locationId);
                const frontEndName = location.storeFrontendName?.value || location.name;
                const storeHidden = location.storeHidden?.value === 'true';
                const storeEnable = location.storeStockAvailability?.value === 'true';

                return {
                    value: frontEndName,
                    label: frontEndName,
                    selected: locationId === currentLocationId,
                    customProperties: {
                        id: locationId,
                        province: location.address.province,
                        storeHidden: storeHidden,
                        storeEnable: storeEnable,
                        stockStatus: stockData.storeStockStatus || 'out-of-stock',
                    },
                };
            })).then(list => list.filter(store =>
                store.customProperties.storeHidden !== true &&
                store.customProperties.storeEnable !== false
            ));

            updatedStoreChoicesList.then(filteredList => {
                this.storeChoicesList = filteredList;
                const selectedProvince = this.provinceSelectionElement?.value || this.currentStoreCookie?.province;
                const filteredChoices = selectedProvince
                    ? filteredList.filter(choice => choice.customProperties.province === selectedProvince)
                    : filteredList;

                if (this.storeChoices) {
                    this.storeChoices.removeActiveItems();
                    this.storeChoices.removeHighlightedItems();
                    this.storeChoices.clearChoices(true, true);
                    this.storeChoices.setChoices(filteredChoices, 'value', 'label', true);
                }
            });
        }

        onVariantChange() {
            document.addEventListener('DOMContentLoaded', () => {
                const container1 = jQuery('[data-section-type="product"]');

                container1.on('variantChange', (event) => {
                    const variant = event.variant || event.detail?.variant;

                    if (variant.id) {
                        this.dataset.variantId = variant.id;
                        this.currentVariantId = variant.id;
                        this.setStockAvailability(variant.id, this.currentLocationId);
                        this.updateStoreChoicesForVariant(variant.id);
                    }
                });
            });
        }
    }
    
    customElements.define('stock-display', StockDisplay);
}
